// Tenantra Inc. - Rent Reporting product page
import { SubPageLayout, Section, FlowSteps, MechanicRows, FAQList } from '@/components/public/SubPageLayout';
import { RentLedgerCard } from '@/components/public/RentLedgerCard';
import { PaymentTimeline } from '@/components/public/PaymentTimeline';

const STEPS = [
  { title: 'Landlord verified', desc: 'Government ID through Didit and an authenticity check on the property ownership document. Only verified landlords can report.' },
  { title: 'Tenant signs consent', desc: 'The tenant receives an e-signature request (SignWell) explaining exactly what will be reported and how to withdraw.' },
  { title: 'Rent is recorded monthly', desc: 'Each month the payment is logged as on time, late or missed, with the amount and any balance owing.' },
  { title: 'Reported to Equifax Canada', desc: 'The payment history is reported in Equifax’s required format. Tenants can see every entry on their record.' },
];

const MECHANICS = [
  { title: 'What gets reported', desc: 'Monthly rent amount, payment status (on time, late, missed), payment date and any balance owing. Nothing about lifestyle, complaints or opinions. Payment facts only.' },
  { title: 'E-signed consent', desc: 'Reporting starts only after the tenant signs the consent document. The signed copy and audit trail (who signed, when, from where) are stored with the tenancy.' },
  { title: 'Withdrawal', desc: 'A tenant can withdraw consent at any time from their record. Reporting stops going forward and the landlord is notified automatically.' },
  { title: 'Disputes', desc: 'A tenant can dispute any entry with evidence. The entry is flagged while under review, the landlord responds, and the outcome is recorded next to the entry.' },
  { title: 'Monthly confirmation', desc: 'Before each report cycle, both landlord and tenant see what is about to be reported, so errors are caught before they reach the bureau.' },
  { title: 'Privacy', desc: 'Records are visible only to the tenant, the reporting landlord and Equifax. Data is stored in Canada and handled under PIPEDA.' },
];

const FAQ = [
  { q: 'Does reporting rent help a tenant’s credit?', a: 'Rent payment history reported to Equifax Canada becomes part of the tenant’s credit file. How it affects a score depends on the scoring model a lender uses. Tenantra does not calculate or display a credit score.' },
  { q: 'What happens when rent is late or missed?', a: 'It is reported as late or missed for that month, with the balance owing. This is why tenants treat rent like any other credit obligation once it is being reported.' },
  { q: 'Can a landlord report without the tenant knowing?', a: 'No. Reporting requires the tenant’s e-signed consent, and the tenant can view every entry and withdraw at any time.' },
  { q: 'Can past months be reported?', a: 'Reporting starts from the date consent is signed. Months before consent are not reported.' },
  { q: 'What happens when the tenant moves out?', a: 'The tenancy is closed with a final status and any balance owing. No further months are reported.' },
];

const SIDES = [
  ['For landlords', 'A verified record that follows the tenant. Fewer late payments, less chasing, and a documented history if things go wrong.'],
  ['For tenants', 'Twelve on-time payments a year that finally count for something, without taking on new debt.'],
  ['For both', 'Facts, not opinions. Dates, amounts and statuses that either side can check and dispute.'],
];

export default function RentReportingPage() {
  return (
    <SubPageLayout
      active="rent-reporting"
      eyebrow="Rent Reporting"
      title="Rent payments, reported to Equifax Canada."
      intro="Every month of rent becomes part of the tenant’s credit history, with their e-signed consent. Tenants build credit with money they already pay. Landlords get tenants who pay on time."
      availability={[{ on: true, text: 'Available now for verified landlords.' }]}
      primary={{ label: 'Start reporting', href: '/register' }}
      secondary={{ label: 'View my rent record', href: '/tenant/access' }}
      image="https://images.unsplash.com/photo-1758523671531-75bd8a19e63b?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=1200"
      imageAlt="Tenants reviewing their rent record on a tablet in their new home"
    >
      <Section eyebrow="How it works" title="Four steps from lease to credit file" testId="reporting-flow">
        <FlowSteps steps={STEPS} />
      </Section>

      <Section tone="sand" eyebrow="The rent record" title="What landlord and tenant both see" intro="One record per tenancy. Each month is logged, then marked as reported once it goes to Equifax." testId="reporting-preview">
        <div className="grid lg:grid-cols-2 gap-10 items-start">
          <RentLedgerCard />
          <PaymentTimeline />
        </div>
        <p className="mt-4 text-sm text-stone-500">Sample tenancies.</p>
      </Section>

      <Section eyebrow="Why it changes behaviour" title="Rent that is reported gets paid on time" intro="Once a tenant knows each month is going on their credit file, rent moves to the top of the list. Late and missed months are recorded as they happen, not argued about months later." testId="reporting-behaviour">
        <div className="grid md:grid-cols-3 gap-10">
          {SIDES.map(([t, d]) => (
            <div key={t}>
              <p className="text-lg font-semibold text-stone-900 mb-2">{t}</p>
              <p className="text-base text-stone-600 leading-relaxed">{d}</p>
            </div>
          ))}
        </div>
      </Section>

      <Section id="consent" tone="sand" eyebrow="Consent, withdrawal and disputes" title="Built around the tenant’s rights" testId="reporting-mechanics">
        <MechanicRows rows={MECHANICS} />
      </Section>

      <Section id="disputes" eyebrow="Questions" title="Common questions" testId="reporting-faq">
        <FAQList items={FAQ} />
      </Section>
    </SubPageLayout>
  );
}
